import { ErrorHandler, Injectable, OnDestroy } from '@angular/core';
import { from } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { SubSink } from 'subsink';
import { BackendAvailabilityService } from '../../backend-availability/backend-availability.service';
import { LogService } from '../../loggers/logger.service';
import { UINotificationService } from '../../ui-notification/uinotification.service';

type CypressAwareGlobal = typeof globalThis & {
  Cypress?: unknown;
  __cypressUnhandledErrors__?: string[];
  __suppressErrorDialogsForCypress__?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class GlobalErrorHandlerService extends ErrorHandler implements OnDestroy {
  private subsink = new SubSink();

  constructor(
    private log: LogService,
    private uiNotifications: UINotificationService,
    private backendAvailability: BackendAvailabilityService,
  ) {
    super();
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public handleError(error: any) {
    this.subsink.sink = from(this.uiNotifications.parseErrorToLogMessage(error))
      .pipe(
        distinctUntilChanged((el1, el2) => el1.logMessage === el2.logMessage)
      )
      .subscribe(({ logMessage, error: parsedError }) => {
        const formattedMessage = `Uncaught exception - ${logMessage}`;
        this.log.error(formattedMessage, parsedError);
        console.error(formattedMessage, parsedError ?? error);
        this.writeCypressErrorLog(formattedMessage, parsedError ?? error);
      });

    if (!this.shouldSuppressErrorDialog()) {
      this.uiNotifications.displayErrorAlert({
        title: 'Unhandled Error',
        message: 'An uncaught exception occurred (see details for more info)',
        details: undefined
      },
      error);
    }
    this.backendAvailability.didErrorsHappen = true;
    this.backendAvailability.checkAvailability();
  }

  private shouldSuppressErrorDialog(): boolean {
    return environment.production === true || this.shouldSuppressErrorDialogForCypress();
  }

  private isRunningUnderCypress(): boolean {
    const maybeGlobal = globalThis as CypressAwareGlobal;

    return !!maybeGlobal.Cypress;
  }

  private shouldSuppressErrorDialogForCypress(): boolean {
    if (!this.isRunningUnderCypress()) {
      return false;
    }

    const maybeGlobal = globalThis as CypressAwareGlobal;

    return maybeGlobal.__suppressErrorDialogsForCypress__ === true;
  }

  private writeCypressErrorLog(message: string, error: unknown): void {
    if (!this.isRunningUnderCypress()) {
      return;
    }

    const maybeGlobal = globalThis as CypressAwareGlobal;
    const existingErrors = maybeGlobal.__cypressUnhandledErrors__ ?? [];
    maybeGlobal.__cypressUnhandledErrors__ = existingErrors;
    existingErrors.push([message, this.stringifyError(error)].filter(Boolean).join('\n'));
  }

  private stringifyError(error: unknown): string {
    if (error instanceof Error) {
      return error.stack ?? error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    if (error === undefined) {
      return '';
    }

    try {
      return JSON.stringify(error);
    } catch {
      return String(error);
    }
  }
}
